import {
  cascadingGetIfNotEmpty,
  getAverageVote,
  getIfNotEmpty,
  isNil,
  notEmpty,
  notNil
} from '@megaphonenpm/mputils/src/utils.js';
import {get, sum} from 'lodash-es';
import {
  PRODUCTNAME_BLANK,
  PRODUCTNAME_SCOREBOARD,
  PRODUCTNAME_WEBPOLL,
  SCENE_TYPE_PREDICTION,
  SCENE_TYPE_QUIZ
} from './constants.js';

export class SceneService {
  constructor () {
  }

  /**
   * Returns the name of the effective product name for the specified scene, in lowercase. The
   * effective product name is the either the screen product name or, if the screen product is
   * "Blank" the controller product name. A "Blank" screen product indicates a "controller only"
   * product (second-screen experience with nothing on the main screen).
   *
   * @param scene
   * @returns {*}
   */
  getProductName (scene) {
    let screenProductName = this.getScreenProductName(scene);
    let controllerProductName = this.getControllerProductName(scene);

    if (screenProductName === PRODUCTNAME_WEBPOLL && controllerProductName === PRODUCTNAME_WEBPOLL) {
      // WEB POLL SCENE
      // For web polls scene, the effective product name is provided at:
      // scene.content.src.controller_product
      return get(scene, 'content.src.controller_product', '').toLowerCase();
    } else {
      // MRTS SCENE
      return screenProductName === PRODUCTNAME_BLANK ? controllerProductName : screenProductName;
    }
  }

  /**
   * For the supplied scene object, returns the current scene's screen product name, in lowercase
   * (e.g., "hotornot"). Otherwise, returns null.
   */
  getScreenProductName (scene) {
    let productName = getIfNotEmpty(scene, 'presentation.screen_product.name');
    productName = isNil(productName) ? null : productName.toLowerCase();
    return productName;
  }

  /**
   * For the supplied scene object, returns the current scene's controller product name, in lowercase
   * (e.g., "hotornot"). Otherwise, returns null.
   */
  getControllerProductName (scene) {
    let productName = getIfNotEmpty(scene, 'presentation.controller_product.name');
    productName = isNil(productName) ? null : productName.toLowerCase();
    return productName;
  }

  isQuizScene (scene) {
    let isQuiz = false;

    if (notNil(scene)) {
      if (scene.type === SCENE_TYPE_QUIZ) {
        isQuiz = true;
      } else {
        // For predictions with an assigned result, the scene type is not, strictly speaking,
        // "SCENE_TYPE_QUIZ", but for the purposes of onscreen display, predictions with an
        // assigned result are treated as quiz scenes.
        let answers = get(scene, 'content.answers');
        if (notEmpty(answers)) {
          let correctAnswers = answers.filter(answer => get(answer, 'correct') === true);
          isQuiz = correctAnswers.length > 0;
        }
      }
    }
    return isQuiz;
  }

  isPredictionScene (scene) {
    return get(scene, 'type') === SCENE_TYPE_PREDICTION;
  }

  isPollScene (scene) {
    // Check if the current scene has any answers. If not, it could be a Teaser, Bulletin,
    // Tweet to TV, or other titling scene. This is necessary because the scene data model
    // specification does not yet strictly indicate the scene type for non-quiz scenes.
    if (get(scene, 'content.answers.length') > 0) {
      return scene.type !== SCENE_TYPE_QUIZ;
    } else {
      return false;
    }
  }

  /**
   * Returns true if the .correct property for ALL answers of the specified scene is null. When
   * .correct is null for all answers, then we infer that the result has not yet been assigned
   * (e.g., in a quiz scene or a prediction scene).
   *
   * Note that a future implementation of the scene data model could, in theory, indicate whether
   * the result is pending more explicitly by setting the scene's .state property to either "closed"
   * or "result". At the time isCorrectResultPending() was added, however, such an implementation
   * was deferred due to the possible destabilizing effect on consumers that expect .state to be
   * one of either "draft", "open", or "closed", but never "result".
   *
   * @param scene
   * @returns {*}
   */
  isCorrectResultPending (scene) {
    return getIfNotEmpty(scene, 'content.answers', []).every(answer => answer.correct === null);
  }

  /**
   * Returns true if the screen product for the specified scene is Web Poll.
   *
   * @param scene
   * @returns {boolean}
   */
  isScreenWebPollScene (scene) {
    return this.getScreenProductName(scene) === PRODUCTNAME_WEBPOLL;
  }

  /**
   * Returns true if the controller product for the specified scene is Web Poll.
   *
   * @param scene
   * @returns {boolean}
   */
  isControllerWebPollScene (scene) {
    return this.getControllerProductName(scene) === PRODUCTNAME_WEBPOLL;
  }

  /**
   * Returns the text of the answer that represents the audience average in a range question.
   * For example, in a Mood Meter, on a scale of 1 to 10, if answer 3 is the average vote, then
   * getTextOfAverageAnswer() will return answer 3's answer text (e.g., "Somewhat opposed").
   *
   * @param scene
   * @param votes An array of "votes per answer" for the specified scene.
   * @returns The text of the average answer.
   */
  getTextOfAverageAnswer (scene, votes) {
    if (notEmpty(scene)) {
      let percent = getAverageVote(votes)/100;
      let answers = get(scene, 'content.answers', []);
      let indexOfAverageAnswer = Math.max(0, Math.round(percent * answers.length) - 1);
      if (answers.length > indexOfAverageAnswer) {
        if (sum(votes) === 0) {
          return answers[0].text;
        } else {
          return answers[indexOfAverageAnswer].text;
        }
      }
    } else {
      return 0;
    }
  }

  /**
   * Given the supplied array of "votes per answer", returns the "star rating" (e.g., 3.5).
   * This is a conveniece method for calculating a star rating based on an array of votes. Most
   * of its work is delegated to getStarRating().
   *
   * @param votes An array of "votes per answer".
   * @returns {*|number} The average vote. E.g., 3.5.
   */
  getStarRatingFromVotes (votes) {
    return this.getStarRating(getAverageVote(votes), votes.length);
  }

  /**
   * Given the current average vote as a percentage (e.g., 75) and the number of stars in the vote,
   * returns the "star rating" to a single decimal place of precision. E.g., for a five-answer
   * question, if the average vote is 3.457104, getStarRating() returns 3.4. When the average is
   * less than 1, getStarRating() removes the leading 0. For example, if the average is 0.3,
   * getStarRating() returns .3 not 0.3. When the average vote is a whole integer, getStarRating()
   * removes the trailing 0. For example, if the average is 3, getStarRating() returns 3, not 3.0.
   *
   * @param averageVoteAsPercent A percentage, such as 34 or 97.
   * @param numStars The number of stars in the vote, such as 5.
   * @returns {*|number} The average vote. E.g., 3.5.
   */
  getStarRating (averageVoteAsPercent, numStars) {
    let currentAverageVote = (averageVoteAsPercent/100) * numStars;
    let singleDecimalAverge = Math.round(currentAverageVote*10)/10;  // E.g., 3.5
    singleDecimalAverge = isNaN(singleDecimalAverge) ? 0 : singleDecimalAverge;
    // When the average is less than 1, strip the leading 0
    singleDecimalAverge = singleDecimalAverge > 0 && singleDecimalAverge < 1 ? singleDecimalAverge.toString().slice(1) : singleDecimalAverge;

    return singleDecimalAverge;
  }

  /**
   * Returns the local MediaAsset for the specified mediaType for the current scene. Used to determine
   * the background, show, and sponsor media for a given scene.
   *
   * Example:
   * backgroundImg.src = getSceneMediaObjectURL('background');
   *
   * @param scene A scene object.
   * @param mediaType A media type, such as 'show', 'sponsor', 'foreground', 'background', etc.
   * @param clientType One of the client-type contants, such as CLIENT_TYPE_SCREEN or CLIENT_TYPE_CONTROLLER.
   */
  getSceneMediaURI (scene, mediaType, clientType) {
    let path = `content.${mediaType}.media.${clientType}.uri`;
    let uri = get(scene, path);
    uri = isNil(uri) || uri === '' ? null : uri;
    return uri;
  }

  /**
   * Returns the answers array for the specified scene, or an empty array if the scene has no answers.
   */
  getAnswers (scene) {
    return get(scene, 'content.answers', [])
  }

  /**
   * Indicates whether the specified scene has a countdown or not.
   *
   * Implementation:
   * Technically, all scenes have a countdown. However, as a temporary means of indicating that a
   * scene should be treated as "permanently paused", Editor sets the scene's main duration to
   * 999999 seconds. Any scene that has a main duration of 999999 should be treated as a scene
   * without a countdown (permanently paused) by all clients, including MegaScreen. In the future,
   * the MRTS protocol will add a legitimate "no countdown" field.
   */
  hasCountdown (scene) {
    if (isNil(scene)) {
      return false;
    } else {
      return parseInt(get(scene, 'timing.main')) < 100000;
    }
  }

  /**
   * Indicates whether the specified scene has a countdown or not.
   * For an important implementation note, see sceneHasCountdown().
   */
  hasResultCountdown (scene) {
    if (isNil(scene)) {
      return false;
    } else {
      let countdownDuration = parseInt(get(scene, 'timing.result'));
      return countdownDuration > 0 && countdownDuration < 100000;
    }
  }

  /**
   * Returns the "scene text," which is the primary text content for the scene, dependent on the
   * scene's type. For example, for polls, the "scene text" is the question text. For social to TV
   * scenes, the "scene text" is the social message (e.g., the Tweet), and so on.
   *
   * @param scene
   * @param defaultText The text to use as the scene text if no text is found in the scene.
   */
  getSceneText (scene, defaultText = '--') {
    let sceneText = cascadingGetIfNotEmpty(scene,
      ['content.question.text',
        'content.title.text',
        'content.social.message.text',
        'content.social.display_name.text',
        'content.subtitle.text',
        'content.note.text']);
    // For Poster, if no "note" is provided, use the foreground media's file name
    if (isNil(sceneText)) {
      let productName = this.getProductName(scene);
      if (notNil(productName) && productName.toLowerCase() === 'poster') {
        sceneText = cascadingGetIfNotEmpty(scene,
          ['content.foreground.media.screen.media_file_name',
            'content.foreground.media.controller.small.media_file_name',
            'content.foreground.media.controller.medium.media_file_name'
          ]);
      }
    }

    // Special case for Prediction scenes: Remove the text "Leaderboard for", which is inserted
    // into the scene by the Prediction CMS when the scene is added to a MRTS playlist.
    if (this.isPredictionScene(scene)
        && this.getProductName(scene).toLowerCase() === PRODUCTNAME_SCOREBOARD) {
      sceneText = sceneText.replace('Leaderboard for ', '');
    }

    // Default "empty" value for scenes with no scene text
    if (isNil(sceneText)) {
      sceneText = defaultText;
    }
    return sceneText;
  }
}

